import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { PageShell } from "@/components/sections";
import { VENDORS } from "@/lib/vendorData";
import { requireMember } from "@/lib/auth";

import { DbVendorProfile } from "@/components/vendor-profile/DbVendorProfile";
import { HardcodedVendorProfile } from "@/components/vendor-profile/HardcodedVendorProfile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/* ---------- Page ---------- */
const VendorProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const hardcoded = VENDORS[slug];
  const [dbVendor, setDbVendor] = useState(null);
  const [dbLoading, setDbLoading] = useState(!hardcoded);
  const [dbMissing, setDbMissing] = useState(false);
  const [showcaseIndex, setShowcaseIndex] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const showcaseRef = useRef(null);
  const showcaseImgRef = useRef(null);

  // Member-only — sign-in + form-completion gate.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { user, redirect } = await requireMember();
      if (cancelled) return;
      if (!user) {
        navigate(`/signin?next=/vendors/${slug}`, { replace: true });
        return;
      }
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }
      setAuthChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, slug]);

  // Fetch the DB vendor as a fallback when the slug isn't in vendorData.
  useEffect(() => {
    if (!authChecked) return;
    if (hardcoded) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/vendors/${slug}`);
        if (!cancelled) setDbVendor(r.data);
      } catch (e) {
        if (!cancelled) setDbMissing(true);
      } finally {
        if (!cancelled) setDbLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, hardcoded, authChecked]);

  // Record a profile-view metric event (best-effort, server dedupes).
  // useRef guards against React StrictMode firing the effect twice in dev.
  const viewTrackedRef = useRef(null);
  useEffect(() => {
    if (!slug) return;
    if (viewTrackedRef.current === slug) return;
    viewTrackedRef.current = slug;
    axios
      .post(`${API}/metrics/event`, { vendor_slug: slug, kind: "view" })
      .catch(() => {});
  }, [slug]);

  const vendor = hardcoded;

  // Subtle parallax on the showcase banner — only when section is in view,
  // GPU-accelerated, and respects prefers-reduced-motion.
  useEffect(() => {
    if (!vendor || !vendor.showcase) return undefined;
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    const section = showcaseRef.current;
    const img = showcaseImgRef.current;
    if (!section || !img) return undefined;

    let raf = 0;
    let visible = false;

    const apply = () => {
      raf = 0;
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const sectionCenter = rect.top + rect.height / 2;
      const delta = sectionCenter - vh / 2;
      // Subtle drift — limit range so the image stays within the cropped frame.
      const range = 70;
      const y = Math.max(-range, Math.min(range, -delta * 0.12));
      img.style.transform = `translate3d(0, ${y}px, 0) scale(1.14)`;
    };

    const onScroll = () => {
      if (!visible || raf) return;
      raf = requestAnimationFrame(apply);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          visible = e.isIntersecting;
          if (visible) apply();
        });
      },
      { threshold: 0 }
    );
    io.observe(section);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    apply();

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [vendor]);

  if (!authChecked) {
    return (
      <PageShell testId={`vendor-profile-auth-${slug}`}>
        <section className="px-6 md:px-12 py-24 text-center">
          <p className="text-sm text-[var(--ld-ink-muted)]">Loading…</p>
        </section>
      </PageShell>
    );
  }

  if (!vendor) {
    if (dbLoading) {
      return (
        <PageShell testId={`vendor-profile-loading-${slug}`}>
          <section className="px-6 md:px-12 py-24 text-center">
            <p className="text-sm text-[var(--ld-ink-muted)]">Loading vendor…</p>
          </section>
        </PageShell>
      );
    }
    if (dbVendor) {
      return (
        <PageShell testId={`vendor-profile-db-${slug}`}>
          <DbVendorProfile vendor={dbVendor} />
        </PageShell>
      );
    }
    if (dbMissing) {
      return <Navigate to="/vendor-listing" replace />;
    }
    return null;
  }

  return (
    <HardcodedVendorProfile
      vendor={vendor}
      slug={slug}
      showcaseIndex={showcaseIndex}
      setShowcaseIndex={setShowcaseIndex}
      showcaseRef={showcaseRef}
      showcaseImgRef={showcaseImgRef}
    />
  );
};

export default VendorProfile;
